/**
 * Every integration, every event, trouble first (doc 07, screen 1).
 *
 * An integration connects one Scout job to one workflow's import command for
 * one event. The tiles on top say how they are doing at a glance, and filter
 * the list; each row shows its recent runs as a strip.
 */

import { useState } from "react";
import { CircleCheck, Pencil, Plus, Power, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { LastRun } from "@/components/integrations/LastRun";
import { RowMenu, useIntegrationActions } from "@/components/integrations/IntegrationActions";
import { useCanWrite, useKeyed, useNow } from "@/components/integrations/hooks";
import {
  Banner,
  Button,
  Card,
  Column,
  Empty,
  HealthPill,
  LinkButton,
  PageHead,
  SearchField,
  Select,
  SkeletonRows,
  Table,
  Td,
  Th,
  Tile,
  Tiles,
  Toolbar,
} from "@/components/kit";
import { listWorkflows } from "@/lib/api/workflows";
import { getScoutJob, listIntegrations, type Integration } from "@/lib/integrations/api";
import {
  HEALTH_KEYS,
  healthBucket,
  healthOf,
  healthWord,
  sendsTo,
  troubleFirst,
  type HealthBucket,
  type HealthKey,
} from "@/lib/integrations/words";

/** Two lines at most; the full sentence is the title. */
const CLAMP = { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } as const;

export function IntegrationsListPage() {
  const now = useNow();
  const canWrite = useCanWrite();
  const list = useKeyed("integrations", () => listIntegrations());
  const workflows = useKeyed("workflows", () => listWorkflows());
  const settingUp = (list.data ?? []).filter((item) => item.state === "setting_up" && item.scoutJobId);
  const jobs = useKeyed(settingUp.length ? settingUp.map((item) => item.code).join(",") : null, () =>
    Promise.all(
      settingUp.map((item) =>
        getScoutJob(item.scoutBaseUrl, item.scoutJobId ?? "").then(
          (job) => [item.code, job.status] as const,
          () => [item.code, null] as const,
        ),
      ),
    ),
  );
  const jobStatus = new Map(jobs.data ?? []);
  const actions = useIntegrationActions(list.reload);

  const [search, setSearch] = useState("");
  const [event, setEvent] = useState("");
  const [workflow, setWorkflow] = useState("");
  const [health, setHealth] = useState<HealthKey | "">("");
  const [bucket, setBucket] = useState<HealthBucket | "">("");

  const names = new Map((workflows.data ?? []).map((row) => [row.code, row.name]));
  const healthFor = (item: Integration) => healthOf(item, { now, scoutJobStatus: jobStatus.get(item.code) ?? null });
  const rows = list.data ?? [];
  const events = [...new Map(rows.map((item) => [item.eventCode, item.eventName])).entries()];
  const workflowCodes = [...new Set(rows.map((item) => item.workflow))];
  const needle = search.trim().toLowerCase();
  const shown = troubleFirst(
    rows.filter(
      (item) =>
        (!needle || item.name.toLowerCase().includes(needle) || item.code.includes(needle) || item.importCommand.includes(needle)) &&
        (!event || item.eventCode === event) &&
        (!workflow || item.workflow === workflow) &&
        (!health || healthFor(item).key === health) &&
        (!bucket || healthBucket(healthFor(item).key) === bucket),
    ),
    healthFor,
  );
  const filtered = Boolean(needle || event || workflow || health || bucket);
  const scoutDown = (jobs.data ?? []).length > 0 && (jobs.data ?? []).every(([, status]) => status === null);

  const tally: Record<HealthBucket, number> = { on_time: 0, needs_look: 0, setting_up: 0, off: 0 };
  let broken = false;
  for (const item of rows) {
    const itemHealth = healthFor(item);
    tally[healthBucket(itemHealth.key)] += 1;
    if (itemHealth.tone === "danger") broken = true;
  }
  const pick = (next: HealthBucket) => {
    setHealth("");
    setBucket(bucket === next ? "" : next);
  };

  return (
    <Column>
      <PageHead
        title="Integrations"
        note="Scout data coming into omnium. Each one connects a Scout job to a workflow’s import, for one event — set up on screens, with no code."
        actions={
          canWrite ? (
            <LinkButton to="/integrations/new" tone="primary">
              <Plus size={15} aria-hidden />
              New integration
            </LinkButton>
          ) : undefined
        }
      />
      {!canWrite && <Banner tone="info">Your account cannot change integrations. You can look at everything.</Banner>}
      {actions.banner}
      {scoutDown && (
        <Banner tone="warning" title="Scout is not answering." actions={<Button onClick={jobs.reload}>Try again</Button>}>
          Settings can still be edited; runs, jobs and previews that need Scout wait.
        </Banner>
      )}
      {list.error && !list.data && (
        <Banner tone="danger" title="The integrations did not load." actions={<Button onClick={list.reload}>Try again</Button>}>
          {list.error}
        </Banner>
      )}

      {list.data && rows.length > 0 && (
        <Tiles>
          <Tile
            label="On time"
            icon={<CircleCheck size={14} aria-hidden />}
            tone={tally.on_time ? "good" : "neutral"}
            value={tally.on_time}
            note="Reading Scout runs as planned"
            onClick={() => pick("on_time")}
            pressed={bucket === "on_time"}
          />
          <Tile
            label="Needs a look"
            icon={<TriangleAlert size={14} aria-hidden />}
            tone={tally.needs_look ? (broken ? "danger" : "warning") : "neutral"}
            value={tally.needs_look}
            note="Failing, late, paused or waiting for you"
            onClick={() => pick("needs_look")}
            pressed={bucket === "needs_look"}
          />
          <Tile
            label="Setting up"
            icon={<Pencil size={14} aria-hidden />}
            value={tally.setting_up}
            note="Not switched on yet"
            onClick={() => pick("setting_up")}
            pressed={bucket === "setting_up"}
          />
          <Tile
            label="Off or paused"
            icon={<Power size={14} aria-hidden />}
            value={tally.off}
            note="Not reading Scout runs"
            onClick={() => pick("off")}
            pressed={bucket === "off"}
          />
        </Tiles>
      )}

      <Toolbar>
        <SearchField value={search} aria-label="Search integrations" placeholder="Search" onChange={(e) => setSearch(e.target.value)} />
        <Select aria-label="Event" style={{ width: "auto", minWidth: "11rem" }} value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="">Event: all</option>
          {events.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </Select>
        <Select aria-label="Workflow" style={{ width: "auto", minWidth: "11rem" }} value={workflow} onChange={(e) => setWorkflow(e.target.value)}>
          <option value="">Workflow: all</option>
          {workflowCodes.map((code) => (
            <option key={code} value={code}>
              {names.get(code) ?? code}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Health"
          style={{ width: "auto", minWidth: "11rem" }}
          value={health}
          onChange={(e) => {
            setBucket("");
            setHealth(e.target.value as HealthKey | "");
          }}
        >
          <option value="">Health: all</option>
          {HEALTH_KEYS.map((key) => (
            <option key={key} value={key}>
              {healthWord(key)}
            </option>
          ))}
        </Select>
        {filtered && (
          <Button
            tone="subtle"
            onClick={() => {
              setSearch("");
              setEvent("");
              setWorkflow("");
              setHealth("");
              setBucket("");
            }}
          >
            Clear
          </Button>
        )}
      </Toolbar>

      {!list.data ? (
        list.error ? null : (
          <Card>
            <SkeletonRows rows={4} columns={5} label="Loading integrations" />
          </Card>
        )
      ) : rows.length === 0 ? (
        <Card>
          <Empty
            title="No integrations yet"
            note="Connect a Scout job to a workflow — no code needed."
            action={canWrite ? <LinkButton to="/integrations/new" tone="primary">New integration</LinkButton> : undefined}
          />
        </Card>
      ) : shown.length === 0 ? (
        <Card>
          <Empty title="No integration matches these filters" note="Clear them to see every integration." />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Integration</Th>
              <Th>Health</Th>
              <Th>Recent runs</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {shown.map((item) => {
              const itemHealth = healthFor(item);
              return (
                <tr key={item.code}>
                  <Td style={{ minWidth: "14rem" }}>
                    <Link to={`/integrations/${encodeURIComponent(item.code)}`} style={{ fontWeight: 600 }}>
                      {item.name}
                    </Link>
                    <div style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)", marginTop: 2 }}>
                      {sendsTo(names.get(item.workflow) ?? item.workflow, item.importCommand)} ·{" "}
                      <Link to={`/events/${encodeURIComponent(item.eventCode)}`}>{item.eventName}</Link>
                    </div>
                    <div style={{ color: "var(--ink-3)", fontSize: "var(--t-xs)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                      {item.code}
                    </div>
                  </Td>
                  <Td style={{ minWidth: "11rem", maxWidth: "19rem" }}>
                    <HealthPill health={itemHealth} count={itemHealth.key === "needs_answer" ? item.openAnswers : undefined} />
                    {itemHealth.key !== "on_time" && (
                      <div title={itemHealth.why} style={{ ...CLAMP, color: "var(--ink-3)", fontSize: "var(--t-sm)", marginTop: 4, overflowWrap: "anywhere" }}>
                        {itemHealth.why}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <LastRun code={item.code} lastRunAt={item.lastRunAt} now={now} />
                  </Td>
                  <Td>
                    <RowMenu item={item} canWrite={canWrite} onAsk={actions.ask} />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Column>
  );
}
